import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import ImportModal from '../components/ImportModal';
import ReadyImportModal from '../components/ReadyImportModal';
import { toast } from '../lib/toast';

type Project = {
  id: string;
  code: string;
  name: string;
  client: string | null;
  city: string | null;
  state: string | null;
  status: string;
  planned_footage: number | null;
  built_meters: number | null;
  total_meters: number | null;
  work_order_count: number | null;
  drop_count: number | null;
  bead_award_round?: string | null;
  bead_state?: string | null;
  subgrant_amount?: number | null;
  bsl_commitment?: number | null;
  deadline?: string | null;
  updated_at?: string;
};

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}k`
  : `$${Math.round(n).toLocaleString()}`;

const STATUS_VARIANT: Record<string, string> = {
  planning: 'pill--info',
  permitting: 'pill--warning',
  construction: 'pill--primary',
  splicing: 'pill--primary',
  testing: 'pill--warning',
  complete: 'pill--success',
  on_hold: 'pill--danger',
};

export default function ProjectsPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showReady, setShowReady] = useState(false);

  const { data: projects = [], isLoading, error } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: () => api.get('/projects'),
  });

  const deleteProjectMut = useMutation({
    mutationFn: (id: string) => api.del(`/projects/${id}`),
    onSuccess: () => {
      toast.success('Project deleted');
      qc.invalidateQueries({ queryKey: ['projects'] });
      // Also clear any stale UI selections
      try {
        localStorage.removeItem('ff.activeZoneId');
        localStorage.removeItem('ff.activeTerritoryPlanId');
      } catch {}
    },
    onError: (e: any) => toast.error('Delete failed: ' + (e?.message ?? e)),
  });

  // Sort: BEAD-tagged first, then most recently created
  const sorted = [...projects].sort((a, b) => {
    const ab = a.subgrant_amount ?? 0;
    const bb = b.subgrant_amount ?? 0;
    if ((ab > 0) !== (bb > 0)) return ab > 0 ? -1 : 1;
    return (b.updated_at ?? '').localeCompare(a.updated_at ?? '');
  });

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Projects</h1>
          <div className="page__subtitle">Every fiber project, by territory and BEAD award round.</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn" onClick={() => setShowReady(true)}>⭱ Import from Ready</button>
          <button className="btn" onClick={() => setShowImport(true)}>⭱ Import CSV/XLSX</button>
          <button className="btn btn--primary" onClick={() => setShowNew(true)}>+ New project</button>
        </div>
      </div>
      {showReady && <ReadyImportModal onClose={() => setShowReady(false)} onDone={() => qc.invalidateQueries({ queryKey: ['projects'] })} />}
      {showImport && <ImportModal title="Import projects" endpoint="/projects/import"
        templateHeaders={['code','name','state','city','client','subgrant_amount','bsl_commitment']}
        sampleRow={{code:'AR-BAXTER-01',name:'Baxter County FTTH',state:'AR',city:'Mountain Home',client:'',subgrant_amount:'5000000',bsl_commitment:'1200'}}
        help="One row per project. Each creates a plant and compliance ledger automatically."
        onClose={() => setShowImport(false)} onDone={() => qc.invalidateQueries({ queryKey: ['projects'] })} />}

      {isLoading && <div style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</div>}
      {error && <div style={{ padding: 24, color: 'var(--danger)' }}>{(error as Error).message}</div>}

      <div className="project-grid">
        {sorted.map((p) => (
          <div key={p.id} className="project-card" onClick={() => nav('/builder')}>
            <div className="project-card__top">
              <div>
                <div className="project-card__code">{p.code}</div>
                <div className="project-card__name">{p.name}</div>
                <div className="project-card__loc">
                  {[p.city, p.state].filter(Boolean).join(', ') || '—'}
                  {p.client ? <> · <span style={{ color: 'var(--text-subtle)' }}>{p.client}</span></> : null}
                </div>
              </div>
              <span className={`pill ${STATUS_VARIANT[p.status] ?? 'pill--info'}`}>{p.status.replace('_', ' ')}</span>
            </div>

            {p.subgrant_amount && p.subgrant_amount > 0 ? (
              <div className="project-card__bead">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                    BEAD {p.bead_state ?? ''} {p.bead_award_round ?? ''}
                  </span>
                  <b style={{ fontSize: 16, color: '#0369a1' }}>{fmtUSD(p.subgrant_amount)}</b>
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
                  {p.bsl_commitment ?? 0} BSLs committed
                  {p.deadline && ` · deadline ${new Date(p.deadline).toLocaleDateString()}`}
                </div>
              </div>
            ) : null}

            <div className="project-card__stats">
              <div className="stat">
                <div className="stat__label">Plant</div>
                <div className="stat__value">{Math.round(((p.total_meters ?? 0) * 3.28084) / 5280 * 10) / 10} mi</div>
              </div>
              <div className="stat">
                <div className="stat__label">Built</div>
                <div className="stat__value">{Math.round(((p.built_meters ?? 0) * 3.28084) / 5280 * 10) / 10} mi</div>
              </div>
              <div className="stat">
                <div className="stat__label">Drops</div>
                <div className="stat__value">{p.drop_count ?? 0}</div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm(`Delete project "${p.name}" and ALL its clusters, segments, and settings? This cannot be undone.`)) {
                    deleteProjectMut.mutate(p.id);
                  }
                }}
                disabled={deleteProjectMut.isPending}
                title="Delete this project and all its data"
                style={{ padding: '4px 10px', fontSize: 11, background: 'transparent', color: '#b91c1c', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer' }}
              >
                {deleteProjectMut.isPending ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={(p) => {
            qc.invalidateQueries({ queryKey: ['projects'] });
            toast.success(`Created ${p.code}`);
            setShowNew(false);
          }}
        />
      )}
    </>
  );
}

function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: Project) => void }) {
  const [form, setForm] = useState({
    name: '', code: '', client: '',
    city: '', state: '',
    status: 'planning',
    bead_award_round: '',
    bead_state: '',
    subgrant_amount: 0,
    bsl_commitment: 0,
    deadline: '',
  });
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const created = await api.post('/projects', {
        ...form,
        subgrant_amount: Number(form.subgrant_amount) || undefined,
        bsl_commitment: Number(form.bsl_commitment) || undefined,
      });

      // Fire-and-forget: ensure the BEAD-6 baseline permits (NEPA / §106 /
      // Tribal / ESA / MBTA / Floodplain) exist as soon as the project does,
      // so the Permits hub isn't empty on first visit. The boot sweep is the
      // safety net if this call fails — we don't block the modal close on it.
      const newProjectId = (created as any)?.id;
      if (newProjectId) {
        api.post(`/permits/projects/${newProjectId}/ensure-baseline`, {})
           .catch(err => console.warn('permits.ensure-baseline failed (will retry on next boot sweep):', err));
      }

      onCreated(created);
    } catch (err) {
      toast.error('Create failed: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const setF = (k: keyof typeof form, v: any) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal__header">
          <span>New project</span>
          <button className="modal__close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal__body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label>Project name</label>
              <input value={form.name} onChange={(e) => setF('name', e.target.value)} required placeholder="Beckley FTTH Phase 2" />
            </div>
            <div className="form-field">
              <label>Project code</label>
              <input value={form.code} onChange={(e) => setF('code', e.target.value)} required placeholder="WV-BCK-002" />
            </div>
            <div className="form-field">
              <label>Status</label>
              <select value={form.status} onChange={(e) => setF('status', e.target.value)}>
                <option>planning</option><option>permitting</option><option>construction</option>
                <option>splicing</option><option>testing</option><option>complete</option><option>on_hold</option>
              </select>
            </div>
            <div className="form-field">
              <label>City</label>
              <input value={form.city} onChange={(e) => setF('city', e.target.value)} placeholder="Beckley" />
            </div>
            <div className="form-field">
              <label>State</label>
              <input value={form.state} onChange={(e) => setF('state', e.target.value)} maxLength={2} />
            </div>
            <div className="form-field" style={{ gridColumn: '1 / -1' }}>
              <label>Client / operator</label>
              <input value={form.client} onChange={(e) => setF('client', e.target.value)} />
            </div>

            <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)', marginBottom: 6 }}>
                BEAD subgrant (optional)
              </div>
            </div>
            <div className="form-field">
              <label>Award round</label>
              <input value={form.bead_award_round} onChange={(e) => setF('bead_award_round', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Subgrant amount ($)</label>
              <input type="number" value={form.subgrant_amount} onChange={(e) => setF('subgrant_amount', e.target.value)} min={0} />
            </div>
            <div className="form-field">
              <label>BSLs committed</label>
              <input type="number" value={form.bsl_commitment} onChange={(e) => setF('bsl_commitment', e.target.value)} min={0} />
            </div>
            <div className="form-field">
              <label>Deadline</label>
              <input type="date" value={form.deadline} onChange={(e) => setF('deadline', e.target.value)} />
            </div>
          </div>
          <div className="modal__footer">
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {busy ? 'Creating…' : 'Create project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
